import React, {
  useState,
  useEffect,
  useCallback,
} from "react";
import styled from "styled-components";
import { useTheme } from "../../context/ThemeContext";
import { getTheme } from "../../theme";
import Button from "../../components/common/Button";
import { api } from "../../services/api";
import {
  Close,
  Add,
  Edit,
  Delete,
} from "@mui/icons-material";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.lg};
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xl};
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  cursor: pointer;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};

  &:hover {
    background-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.hover};
  }
`;

const Section = styled.div`
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xl};
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const InfoItem = styled.div`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surfaceElevated};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
`;

const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
`;

const WhatsNewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const WhatsNewCard = styled.div`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surfaceElevated};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
`;

const WhatsNewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const WhatsNewTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
`;

const WhatsNewDescription = styled.div`
  font-size: 14px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const WhatsNewMeta = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  font-size: 12px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const ActionButton = styled.button`
  padding: 4px 8px;
  border: none;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    props.variant === "danger"
      ? "#EF4444"
      : getTheme(props.theme?.isDark).colors.primary};
  color: white;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    opacity: 0.9;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xl};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
`;

const AppVersionDetailsModal = ({
  isOpen,
  onClose,
  version,
  onWhatsNewCreate,
}) => {
  const { isDark } = useTheme();
  const theme = { isDark };

  const [whatsNewEntries, setWhatsNewEntries] = useState(
    []
  );
  const [loading, setLoading] = useState(false);

  const fetchWhatsNew = useCallback(async () => {
    if (!version?._id && !version?.id) return;

    setLoading(true);
    try {
      const data = await api.getWhatsNew({
        filter: { appVersionId: version._id || version.id },
      });
      setWhatsNewEntries(data.results || []);
    } catch (err) {
      console.error("Error fetching What's New:", err);
    } finally {
      setLoading(false);
    }
  }, [version]);

  useEffect(() => {
    if (isOpen && version) {
      fetchWhatsNew();
    }
  }, [isOpen, version, fetchWhatsNew]);

  const handleDeleteWhatsNew = async (whatsNewId) => {
    if (
      !window.confirm(
        "Bu What's New entry-sini silmək istədiyinizə əminsiniz?"
      )
    ) {
      return;
    }

    try {
      await api.deleteWhatsNew(whatsNewId);
      fetchWhatsNew();
    } catch (err) {
      alert(
        "Xəta: " +
          (err.response?.data?.message || err.message)
      );
    }
  };

  if (!isOpen || !version) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent
        theme={theme}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}>
            App Version Detalları - {version.platform} v
            {version.latestVersion}
          </ModalTitle>
          <CloseButton theme={theme} onClick={onClose}>
            <Close />
          </CloseButton>
        </ModalHeader>

        <Section theme={theme}>
          <SectionTitle theme={theme}>
            Version Məlumatları
          </SectionTitle>
          <InfoGrid theme={theme}>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Platform</InfoLabel>
              <InfoValue theme={theme}>
                {version.platform}
              </InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>
                Latest Version
              </InfoLabel>
              <InfoValue theme={theme}>
                {version.latestVersion}
              </InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>
                Min Required Version
              </InfoLabel>
              <InfoValue theme={theme}>
                {version.minRequiredVersion}
              </InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>
                Force Update
              </InfoLabel>
              <InfoValue theme={theme}>
                {version.forceUpdate ? "Bəli" : "Xeyr"}
              </InfoValue>
            </InfoItem>
            {version.appFilePath && (
              <InfoItem theme={theme}>
                <InfoLabel theme={theme}>
                  App File
                </InfoLabel>
                <InfoValue theme={theme}>
                  {version.appFileName}
                </InfoValue>
              </InfoItem>
            )}
            {version.lambdatestAppId && (
              <InfoItem theme={theme}>
                <InfoLabel theme={theme}>
                  LambdaTest App ID
                </InfoLabel>
                <InfoValue theme={theme}>
                  {version.lambdatestAppId}
                </InfoValue>
              </InfoItem>
            )}
          </InfoGrid>
        </Section>

        <Section theme={theme}>
          <SectionTitle theme={theme}>
            What's New Entries
            <Button
              variant="primary"
              theme={theme}
              onClick={() => {
                if (onWhatsNewCreate) {
                  onWhatsNewCreate(version);
                }
              }}
              style={{
                fontSize: "12px",
                padding: "6px 12px",
              }}
            >
              <Add /> Yeni What's New
            </Button>
          </SectionTitle>

          {loading ? (
            <EmptyState theme={theme}>
              Yüklənir...
            </EmptyState>
          ) : whatsNewEntries.length === 0 ? (
            <EmptyState theme={theme}>
              What's New entry-si yoxdur. Yeni entry
              yaradın.
            </EmptyState>
          ) : (
            <WhatsNewList theme={theme}>
              {whatsNewEntries.map((entry) => (
                <WhatsNewCard
                  key={entry._id || entry.id}
                  theme={theme}
                >
                  <WhatsNewHeader theme={theme}>
                    <div>
                      <WhatsNewTitle theme={theme}>
                        {entry.title}
                      </WhatsNewTitle>
                      <WhatsNewMeta theme={theme}>
                        <span>
                          Version: {entry.version}
                        </span>
                        <span>
                          Priority: {entry.priority}
                        </span>
                        <span>
                          Active:{" "}
                          {entry.isActive ? "Bəli" : "Xeyr"}
                        </span>
                      </WhatsNewMeta>
                    </div>
                    <ActionButtons theme={theme}>
                      <ActionButton
                        theme={theme}
                        onClick={() => {
                          // TODO: Edit functionality
                          alert(
                            "Edit functionality coming soon"
                          );
                        }}
                      >
                        <Edit
                          style={{ fontSize: "14px" }}
                        />
                      </ActionButton>
                      <ActionButton
                        theme={theme}
                        variant="danger"
                        onClick={() =>
                          handleDeleteWhatsNew(
                            entry._id || entry.id
                          )
                        }
                      >
                        <Delete
                          style={{ fontSize: "14px" }}
                        />
                      </ActionButton>
                    </ActionButtons>
                  </WhatsNewHeader>
                  <WhatsNewDescription theme={theme}>
                    {entry.description}
                  </WhatsNewDescription>
                  {entry.features &&
                    entry.features.length > 0 && (
                      <div
                        style={{
                          marginTop:
                            getTheme(isDark).spacing.sm,
                        }}
                      >
                        {entry.features.map(
                          (feature, idx) => (
                            <div
                              key={idx}
                              style={{
                                display: "flex",
                                gap: getTheme(isDark)
                                  .spacing.xs,
                                marginBottom:
                                  getTheme(isDark).spacing
                                    .xs,
                              }}
                            >
                              <span>{feature.emoji}</span>
                              <span
                                style={{
                                  fontSize: "14px",
                                  color:
                                    getTheme(isDark).colors
                                      .textSecondary,
                                }}
                              >
                                {feature.description}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    )}
                </WhatsNewCard>
              ))}
            </WhatsNewList>
          )}
        </Section>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AppVersionDetailsModal;
